import { type EnterpriseSummary } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  GearSix,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  Text,
  TextField,
} from "@radix-ui/themes";
import cx from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CreateEnterpriseDialog } from "./CreateEnterpriseDialog";
import { DuplicateEnterpriseDialog } from "./DuplicateEnterpriseDialog";
import { RenameEnterpriseDialog } from "./RenameEnterpriseDialog";

import { useUI } from "../contexts/ui";
import { ssoAuthorize } from "../services/auth";
import { useAnalytics } from "../utils/analytics";
import { canAddEnterprise, isMeaningfulGigsRole } from "../utils/auth";
import { onSwitchCompleted } from "../utils/enterpriseSwitch";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import {
  useDeleteEnterprise,
  useEnterprises,
  useSwitchEnterprise,
  useUpdateEnterpriseIsDemo,
} from "../utils/queries/enterprises";

const DELETE_CONFIRM_PHRASE = "delete";

function matchesEnterprisePrefix(name: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return name.toLowerCase().startsWith(trimmed);
}

function sortByName(enterprises: EnterpriseSummary[]): EnterpriseSummary[] {
  return [...enterprises].sort((a, b) => a.name.localeCompare(b.name));
}

export default function EnterpriseSwitcher({
  className,
  allowAddEnterprise = true,
  disabled = false,
}: {
  className?: string;
  allowAddEnterprise?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation("components");
  const analytics = useAnalytics("EnterpriseSwitcher");
  const { notify } = useUI();

  const enterprisesQuery = useEnterprises();
  const switchEnterpriseMutation = useSwitchEnterprise();
  const deleteEnterpriseMutation = useDeleteEnterprise();
  const updateIsDemoMutation = useUpdateEnterpriseIsDemo();

  const userEnterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );
  const userEmail = useAppSelector((state) => state.auth.value?.email);

  const [createEnterpriseDialogOpen, setCreateEnterpriseDialogOpen] =
    useState(false);
  const [duplicateSource, setDuplicateSource] =
    useState<EnterpriseSummary | null>(null);
  const [renameTarget, setRenameTarget] = useState<EnterpriseSummary | null>(
    null,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<EnterpriseSummary | null>(
    null,
  );
  const [deleteConfirm, setDeleteConfirm] = useState("");

  // Strict MG staff role only — never isMeaningfulGigsOrDemo(). Demo personas
  // and rare non-MG users with the enterprise picker must not see lifecycle
  // controls (promote / demote / delete). Backend enforces the same gate.
  const isMg = isMeaningfulGigsRole();

  const { data } = enterprisesQuery;
  const currentEnterprise = data?.find((e) => e._id === userEnterpriseId);

  const { productionEnterprises, demoEnterprises } = useMemo(() => {
    const list = data ?? [];
    const filtered = list.filter((e) =>
      matchesEnterprisePrefix(e.name, searchQuery),
    );
    return {
      // Missing isDemo (legacy) === production
      productionEnterprises: sortByName(
        filtered.filter((e) => e.isDemo !== true),
      ),
      demoEnterprises: sortByName(filtered.filter((e) => e.isDemo === true)),
    };
  }, [data, searchQuery]);

  if (enterprisesQuery.isPending || enterprisesQuery.isError || !data) {
    return null;
  }

  if (data.length === 1 && !canAddEnterprise()) {
    return (
      <Text size="2" weight="medium" className="truncate">
        {currentEnterprise?.name}
      </Text>
    );
  }

  async function startSsoForEnterprise(enterpriseId: string) {
    if (!userEmail) {
      notify({
        variant: "error",
        title: "SSO required",
        message: "Sign in again, then switch to this enterprise.",
      });
      return;
    }
    try {
      const policy = await ssoAuthorize({
        email: userEmail,
        enterpriseId,
      });
      if (policy?.redirectUrl) {
        window.location.href = policy.redirectUrl;
        return;
      }
      notify({
        variant: "error",
        title: "SSO required",
        message:
          "This enterprise requires SSO. Contact your admin if the redirect failed.",
      });
    } catch (err) {
      notify({
        variant: "error",
        title: "SSO required",
        message:
          err instanceof Error
            ? err.message
            : "Re-authenticate with SSO before switching.",
      });
    }
  }

  // `switchEnterprise` goes through smartFetch, which rejects with the raw
  // `Response` on any non-ok status — not an Error. `error instanceof Error`
  // was therefore false for every server-side switch failure, so `String(error)`
  // produced the literal string "[object Response]". That both showed the user
  // "[object Response]" as the toast body and made the reactive SSO re-auth
  // branch dead code: the 403 "SSO required" text lives in the JSON body, which
  // has to be read off the Response. `errorMessage` is the helper that does so.
  async function reportSwitchFailure(enterpriseId: string, error: unknown) {
    const message = (await errorMessage(error)).trim();
    if (/SSO required/i.test(message)) {
      await startSsoForEnterprise(enterpriseId);
      return;
    }
    notify({
      variant: "error",
      title: "Could not switch enterprise",
      message,
    });
  }

  function handleSwitchEnterprise(enterpriseId: string) {
    if (enterpriseId !== userEnterpriseId) {
      const start = performance.now();
      const target = data?.find((e) => e._id === enterpriseId);
      // Proactive SSO for enforced tenants (MG staff bypass switch-guard server-side).
      if (target?.auth?.ssoMode === "enforced" && !isMeaningfulGigsRole()) {
        void startSsoForEnterprise(enterpriseId);
        return;
      }

      switchEnterpriseMutation.mutate(enterpriseId, {
        onSuccess: (outcome) => {
          // why-not: no `switch_enterprise` capture for the MFA step arm,
          // deliberately. The switch has not happened yet — it happens when the
          // code is verified — so recording it there would report a switch that
          // the user may yet cancel. The dialog captures
          // `authentication_dialog_mfa_verification` when the step completes,
          // which is the real completion event; the funnel's MFA segment is
          // read from there, not from this metric.
          onSwitchCompleted(outcome, {
            onSwitched: () => {
              analytics.capture("switch_enterprise", {
                from: userEnterpriseId,
                to: enterpriseId,
                duration_ms: performance.now() - start,
                user: userEmail,
              });
              location.reload();
            },
          });
        },
        onError: (error) => {
          void reportSwitchFailure(enterpriseId, error);
        },
      });
    }
  }

  async function handleSetDemo(enterprise: EnterpriseSummary, isDemo: boolean) {
    if (!isMeaningfulGigsRole()) return;
    try {
      await updateIsDemoMutation.mutateAsync({
        enterpriseId: enterprise._id,
        isDemo,
      });
      notify({
        variant: "success",
        title: isDemo ? "Demoted to demo" : "Promoted to production",
        message: isDemo
          ? `${enterprise.name} is now listed under Demo.`
          : `${enterprise.name} is now listed under Production.`,
      });
      if (enterprise._id === userEnterpriseId) {
        location.reload();
      }
    } catch {
      notify({
        variant: "error",
        title: "Error",
        message: isDemo
          ? `Failed to demote ${enterprise.name}.`
          : `Failed to promote ${enterprise.name}.`,
      });
    }
  }

  function closeDeleteDialog() {
    setPendingDelete(null);
    setDeleteConfirm("");
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    if (!isMeaningfulGigsRole()) {
      closeDeleteDialog();
      return;
    }
    if (pendingDelete._id === userEnterpriseId) {
      closeDeleteDialog();
      notify({
        variant: "error",
        title: "Cannot delete active enterprise",
        message:
          "Switch away first, or delete from Enterprise settings (which switches you automatically).",
      });
      return;
    }
    if (deleteConfirm.trim().toLowerCase() !== DELETE_CONFIRM_PHRASE) return;

    const target = pendingDelete;
    closeDeleteDialog();

    try {
      await deleteEnterpriseMutation.mutateAsync({
        enterpriseId: target._id,
        cascade: target.isDemo === true,
      });
      notify({
        variant: "success",
        title: "Enterprise deleted",
        message: `Deleted ${target.name}.`,
      });
    } catch {
      notify({
        variant: "error",
        title: "Error",
        message: `Failed to delete ${target.name}.`,
      });
    }
  }

  function renderEnterpriseItem(enterprise: EnterpriseSummary) {
    const showDelete = isMg && enterprise._id !== userEnterpriseId;
    const isDemo = enterprise.isDemo === true;

    // Gear SubTrigger MUST stay outside DropdownMenu.Item. Nesting actions
    // inside the Item lets Radix fire select (switch + reload) on click.
    return (
      <div
        key={enterprise._id}
        className="group relative max-w-60"
        data-testid={`enterprise-menu-row-${enterprise._id}`}
      >
        <DropdownMenu.Item
          onSelect={() => handleSwitchEnterprise(enterprise._id)}
          data-testid={`enterprise-menu-item-${enterprise._id}`}
          color={enterprise._id === userEnterpriseId ? "blue" : undefined}
          // `peer` drives gear color from this name row (sibling SubTrigger).
          className={cx("peer w-full cursor-pointer", isMg && "pr-6")}
        >
          <span className="block min-w-0 truncate pl-2">{enterprise.name}</span>
        </DropdownMenu.Item>
        {isMg ? (
          <DropdownMenu.Sub>
            {/*
              Themes SubTrigger is a full menu item + chevron shortcut. Override
              to an icon-only gear and hide .rt-BaseMenuShortcut (the ">" ).
              Rendered only when isMeaningfulGigsRole() — not for demo widen.
              Colors use Carrie tokens (text-white / text-gray-12 are not in the
              Tailwind theme — those classes were no-ops on epic).
            */}
            <DropdownMenu.SubTrigger
              data-testid={`enterprise-settings-${enterprise._id}`}
              aria-label={`Manage ${enterprise.name}`}
              className={cx(
                "absolute right-1 top-1/2 z-10 m-0 flex size-6 min-h-0 max-w-6 -translate-y-1/2 cursor-pointer items-center justify-center overflow-hidden rounded outline-none",
                "!bg-transparent !p-0 data-[highlighted]:!bg-transparent data-[state=open]:!bg-transparent",
                "[&_.rt-BaseMenuShortcut]:!hidden",
                "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 group-has-[[data-highlighted]]:opacity-100 data-[state=open]:opacity-100",
                // Name row highlighted/hovered → white on blue. Gear/submenu → black.
                "!text-puntt-cool-gray-12",
                "peer-hover:!text-base-white peer-data-[highlighted]:!text-base-white",
                "hover:!text-puntt-cool-gray-12 data-[highlighted]:!text-puntt-cool-gray-12 data-[state=open]:!text-puntt-cool-gray-12",
                "[&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:!fill-current",
              )}
            >
              <GearSix weight="bold" aria-hidden />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent
              data-testid={`enterprise-settings-menu-${enterprise._id}`}
              className="cursor-pointer [&_.rt-BaseMenuItem]:cursor-pointer"
            >
              {isDemo ? (
                <DropdownMenu.Item
                  data-testid={`enterprise-promote-${enterprise._id}`}
                  className="cursor-pointer"
                  onSelect={() => {
                    void handleSetDemo(enterprise, false);
                  }}
                >
                  <Flex align="center" gap="2">
                    <ArrowUp
                      weight="bold"
                      className="size-3.5 shrink-0"
                      aria-hidden
                    />
                    Promote to production
                  </Flex>
                </DropdownMenu.Item>
              ) : (
                <DropdownMenu.Item
                  data-testid={`enterprise-demote-${enterprise._id}`}
                  className="cursor-pointer"
                  onSelect={() => {
                    void handleSetDemo(enterprise, true);
                  }}
                >
                  <Flex align="center" gap="2">
                    <ArrowDown
                      weight="bold"
                      className="size-3.5 shrink-0"
                      aria-hidden
                    />
                    Demote to demo
                  </Flex>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                data-testid={`enterprise-rename-${enterprise._id}`}
                className="cursor-pointer"
                onSelect={() => {
                  setMenuOpen(false);
                  setRenameTarget(enterprise);
                }}
              >
                <Flex align="center" gap="2">
                  <PencilSimple
                    weight="bold"
                    className="size-3.5 shrink-0"
                    aria-hidden
                  />
                  {t("enterpriseSwitcher.rename")}
                </Flex>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-testid={`enterprise-duplicate-${enterprise._id}`}
                className="cursor-pointer"
                onSelect={() => {
                  setMenuOpen(false);
                  setDuplicateSource(enterprise);
                }}
              >
                <Flex align="center" gap="2">
                  <Copy
                    weight="bold"
                    className="size-3.5 shrink-0"
                    aria-hidden
                  />
                  Duplicate…
                </Flex>
              </DropdownMenu.Item>
              {showDelete && (
                <DropdownMenu.Item
                  color="red"
                  data-testid={`enterprise-delete-${enterprise._id}`}
                  className="cursor-pointer"
                  onSelect={() => {
                    setMenuOpen(false);
                    setPendingDelete(enterprise);
                  }}
                >
                  <Flex align="center" gap="2">
                    <Trash
                      weight="bold"
                      className="size-3.5 shrink-0"
                      aria-hidden
                    />
                    Delete…
                  </Flex>
                </DropdownMenu.Item>
              )}
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <CreateEnterpriseDialog
        open={createEnterpriseDialogOpen}
        onOpenChange={setCreateEnterpriseDialogOpen}
      />

      <RenameEnterpriseDialog
        open={renameTarget != null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        enterprise={renameTarget}
      />

      <DuplicateEnterpriseDialog
        open={duplicateSource != null}
        onOpenChange={(open) => {
          if (!open) setDuplicateSource(null);
        }}
        sourceEnterprise={duplicateSource}
      />

      <Dialog.Root
        open={pendingDelete != null}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
      >
        <Dialog.Content
          style={{ maxWidth: 420 }}
          data-testid="enterprise-delete-confirm-dialog"
        >
          <Dialog.Title>
            {t("enterpriseSwitcher.deleteEnterprise")}
          </Dialog.Title>
          <Text as="p" size="2" mb="3">
            Permanently delete {pendingDelete?.name}? This removes the
            enterprise for everyone, including profiles and access. This cannot
            be undone.
          </Text>
          <label htmlFor="enterprise-switcher-delete-confirm">
            <Text as="div" size="2" mb="1" weight="bold">
              {`Type ${DELETE_CONFIRM_PHRASE} to confirm`}
            </Text>
            <TextField.Root
              id="enterprise-switcher-delete-confirm"
              data-testid="enterprise-delete-confirm-input"
              aria-label={`Type ${DELETE_CONFIRM_PHRASE} to confirm deletion`}
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={DELETE_CONFIRM_PHRASE}
              autoComplete="off"
              mb="4"
            />
          </label>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                {t("common:actions.cancel")}
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              color="red"
              data-testid="enterprise-delete-confirm-button"
              disabled={
                deleteConfirm.trim().toLowerCase() !== DELETE_CONFIRM_PHRASE
              }
              onClick={() => void confirmDelete()}
              loading={deleteEnterpriseMutation.isPending}
            >
              {t("enterpriseSwitcher.deleteEnterprise2")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <DropdownMenu.Root
        open={menuOpen}
        onOpenChange={(open) => {
          setMenuOpen(open);
          if (!open) setSearchQuery("");
        }}
      >
        <DropdownMenu.Trigger
          className={cx("flex-1", className)}
          disabled={disabled || switchEnterpriseMutation.isPending}
          data-testid="enterprise-switcher-trigger"
        >
          <Button
            size="1"
            variant="outline"
            color="gray"
            className="min-w-0 justify-between"
            loading={switchEnterpriseMutation.isPending}
          >
            <span className="truncate">{currentEnterprise?.name}</span>

            <DropdownMenu.TriggerIcon />
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          align="end"
          data-testid="enterprise-switcher-dropdown-menu"
          className={cx("max-h-[min(70vh,420px)] overflow-y-auto", className)}
        >
          <div
            className="px-2 py-1"
            data-testid="enterprise-switcher-search-wrap"
          >
            <TextField.Root
              size="1"
              placeholder={t("enterpriseSwitcher.searchEnterprises")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid="enterprise-switcher-search"
              aria-label={t("enterpriseSwitcher.searchEnterprises")}
              autoFocus
            />
          </div>

          <div data-testid="selectable-enterprise-group">
            {productionEnterprises.length > 0 && (
              <DropdownMenu.Group data-testid="enterprise-group-production">
                <DropdownMenu.Label
                  data-testid="enterprise-group-label-production"
                  className="text-2 pt-1 font-medium"
                >
                  {t("enterpriseSwitcher.production")}
                </DropdownMenu.Label>
                {productionEnterprises.map(renderEnterpriseItem)}
              </DropdownMenu.Group>
            )}

            {demoEnterprises.length > 0 && (
              <DropdownMenu.Group data-testid="enterprise-group-demo">
                <DropdownMenu.Label
                  data-testid="enterprise-group-label-demo"
                  className="text-2 pt-1 font-medium"
                >
                  {t("enterpriseSwitcher.demo")}
                </DropdownMenu.Label>
                {demoEnterprises.map(renderEnterpriseItem)}
              </DropdownMenu.Group>
            )}
          </div>

          {canAddEnterprise() && allowAddEnterprise && (
            <>
              <DropdownMenu.Separator />

              <DropdownMenu.Group data-testid="enterprise-switcher-actions-group">
                <DropdownMenu.Item
                  onClick={() => setCreateEnterpriseDialogOpen(true)}
                  data-testid="create-enterprise-menu-item"
                  className="cursor-pointer"
                >
                  {t("enterpriseSwitcher.addEnterprise")}
                </DropdownMenu.Item>
              </DropdownMenu.Group>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </>
  );
}
